import React, { useState } from 'react';
import { X, Package, MapPin, Phone, User, Calendar, Clock, Truck, CheckCircle, AlertCircle, MessageSquare } from 'lucide-react';
import { barrelService } from '../../../services/barrelService';
import Icon from '../../../components/AppIcon';


export default function BarrelDepositDetailModal({ deposit, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState(deposit?.delivery_notes || '');
  const [updating, setUpdating] = useState(false);

  const handleStartDelivery = async () => {
    try {
      setUpdating(true);
      setError('');

      await barrelService?.updateBarrelDeposit(deposit?.id, {
        collection_status: 'in_progress',
        delivery_notes: deliveryNotes
      });

      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error('Error starting delivery:', err);
      setError('Erreur lors du démarrage de la livraison');
    } finally {
      setUpdating(false);
    }
  };

  const handleCompleteDelivery = async () => {
    try {
      setUpdating(true);
      setError('');

      // Update deposit status to collected and payment to paid
      await barrelService?.updateBarrelDeposit(deposit?.id, {
        collection_status: 'collected',
        payment_status: 'paid',
        delivery_date: new Date()?.toISOString()?.split('T')?.[0],
        delivery_notes: deliveryNotes
      });

      // CRITICAL: Also update the linked barrel_sale payment status
      if (deposit?.sale_id) {
        await barrelService?.updateSale(deposit?.sale_id, {
          paymentStatus: 'paid',
          orderStatus: 'delivered'
        });
      }

      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error('Error completing delivery:', err);
      setError('Erreur lors de la validation de la livraison');
    } finally {
      setUpdating(false);
    }
  };

  const getBarrelTypeLabel = (type) => {
    return type === 'noir_270l' ? 'Barrique Noire 270L' : 'Barrique Bleue 220L';
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      scheduled: { label: 'Planifié', color: 'bg-blue-100 text-blue-800', icon: Calendar },
      in_progress: { label: 'En cours', color: 'bg-orange-100 text-orange-800', icon: Truck },
      collected: { label: 'Déposé', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-800', icon: AlertCircle }
    };
    const badge = badges?.[status] || badges?.pending;
    const Icon = badge?.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${badge?.color}`}>
        <Icon className="w-4 h-4" />
        {badge?.label}
      </span>
    );
  };

  const unitPrice = deposit?.barrel_type === 'noir_270l' ? 35 : 30;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white/20 rounded-lg p-3">
              <Package className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Détails du Dépôt</h2>
              <p className="text-sm text-orange-100">
                {getBarrelTypeLabel(deposit?.barrel_type)} × {deposit?.quantity}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Statut actuel</span>
            {getStatusBadge(deposit?.collection_status)}
          </div>

          {/* Customer Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5 text-gray-600" />
              Informations Client
            </h3>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <User className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{deposit?.client_name}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <a href={`tel:${deposit?.client_phone}`} className="text-sm text-blue-600 hover:underline">
                    {deposit?.client_phone}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-700">{deposit?.client_address}</p>
                  {deposit?.client_city && (
                    <p className="text-sm text-gray-500">{deposit?.client_city}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Barrel Details */}
          <div className="bg-blue-50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Détails de la Livraison
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500">Type de barrique</p>
                <p className="text-sm font-medium text-gray-900">{getBarrelTypeLabel(deposit?.barrel_type)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Quantité</p>
                <p className="text-sm font-medium text-gray-900">{deposit?.quantity}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Prix unitaire</p>
                <p className="text-sm font-medium text-gray-900">{unitPrice}€</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Prix total</p>
                <p className="text-sm font-bold text-blue-600">{deposit?.total_price}€</p>
              </div>
            </div>
            {deposit?.planned_collection_date && (
              <div className="flex items-center gap-2 pt-2 border-t border-blue-100">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-gray-700">
                  Prévu le {new Date(deposit?.planned_collection_date)?.toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
            )}
            {deposit?.time_slot && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-gray-700">Créneau: {deposit?.time_slot}</span>
              </div>
            )}
          </div>

          {/* Delivery Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Commentaires / Notes de livraison
            </label>
            <textarea
              value={deliveryNotes}
              onChange={(e) => setDeliveryNotes(e?.target?.value)}
              placeholder="Ajoutez des commentaires sur la livraison, l'état du client, etc."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-3">
          {deposit?.collection_status === 'pending' || deposit?.collection_status === 'scheduled' ? (
            <button
              onClick={handleStartDelivery}
              disabled={updating}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {updating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Mise à jour...</span>
                </>
              ) : (
                <>
                  <Truck className="w-5 h-5" />
                  <span>Livraison en cours</span>
                </>
              )}
            </button>
          ) : null}

          {deposit?.collection_status === 'in_progress' ? (
            <button
              onClick={handleCompleteDelivery}
              disabled={updating}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {updating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Validation...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Livraison validée et payée</span>
                </>
              )}
            </button>
          ) : null}

          {deposit?.collection_status === 'collected' ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm font-semibold text-green-800">Livraison terminée et payée</p>
              {deposit?.delivery_date && (
                <p className="text-xs text-green-600 mt-1">
                  Livrée le {new Date(deposit?.delivery_date)?.toLocaleDateString('fr-FR')}
                </p>
              )}
            </div>
          ) : null}

          <button
            onClick={onClose}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}